import { FC } from "react";
import { Navigate } from "react-router-dom";
import { Stack, Typography } from "@mui/material";
import { Lock } from "@mui/icons-material";

import { useAppSelector } from "@/store";
import { useGetTenantAllowedPagesQuery } from "@/graphql/page-access";
import { roles } from "@/roles";
import { tokens } from "@/tokens";
import { hasPageAccess } from "./pageAccess";

interface Props {
  tabs: { link: string; pageKey: string }[];
  fallback?: string;
}

// Resolves the first tab the current user can access. Prevents forced
// redirects to unrelated pages when a section's hardcoded default tab is
// gated off. Shows a permission-denied card if the section is fully empty.
export const FirstAllowedTabRedirect: FC<Props> = ({ tabs, fallback }) => {
  const { user } = useAppSelector((s) => s.auth);
  const shouldFetch =
    !!user?.userRole &&
    [roles.SPOWNER, roles.SPPARTNER, roles.SPCLIENT, roles.TDCLIENT].includes(
      user.userRole
    );
  const { data: tenantPagesResp, isLoading } = useGetTenantAllowedPagesQuery(
    undefined,
    { skip: !shouldFetch }
  );

  if (shouldFetch && isLoading) return null;

  const userPages = user?.allowedPages as string[] | undefined;
  const pages = userPages ?? tenantPagesResp?.pages;

  // TD_ADMIN (and pre-hydration SP roles) have no page list to check
  // against; match useRequirePage semantics and allow-all. Pick the first
  // tab in order.
  if (!pages) {
    const first = tabs[0];
    if (first) return <Navigate to={first.link} replace />;
  }

  const allowed = tabs.find(
    (t) => t.pageKey === "__ALL__" || (pages && hasPageAccess(pages, t.pageKey))
  );
  if (allowed) {
    return <Navigate to={allowed.link} replace />;
  }
  if (fallback) {
    return <Navigate to={fallback} replace />;
  }
  return (
    <Stack
      alignItems="center"
      sx={{ p: 5, borderRadius: 1, background: tokens.bg.muted }}
      spacing={1.5}
    >
      <Lock fontSize="large" sx={{ color: tokens.brand.primary }} />
      <Typography variant="h5" sx={{ color: tokens.brand.primary }}>
        No accessible tabs
      </Typography>
      <Typography variant="body1">
        You don&apos;t have permission to view any tabs in this section.
      </Typography>
    </Stack>
  );
};
